import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';
import './SearchableSelect.css';

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: string | string[];
  onChange: (value: any) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isMulti?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select option...',
  className = '',
  disabled = false,
  isMulti = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Derived state for selected labels
  const getSelectedLabels = () => {
    if (isMulti) {
      const values = Array.isArray(value) ? value : [];
      return options.filter(opt => values.includes(opt.value));
    } else {
      const opt = options.find(opt => opt.value === value);
      return opt ? [opt] : [];
    }
  };

  const selectedOptions = getSelectedLabels();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (val: string) => {
    if (isMulti) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(val)
        ? currentValues.filter(v => v !== val)
        : [...currentValues, val];
      onChange(newValues);
    } else {
      onChange(val);
      setIsOpen(false);
    }
    setSearchTerm('');
  };

  const handleRemove = (e: React.MouseEvent, val: string) => {
    e.stopPropagation();
    if (isMulti) {
      const currentValues = Array.isArray(value) ? value : [];
      onChange(currentValues.filter(v => v !== val));
    } else {
      onChange('');
    }
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(isMulti ? [] : '');
  };

  return (
    <div className={`searchable-select-container ${className} ${isMulti ? 'multi' : ''}`} ref={containerRef}>
      <div
        className={`searchable-select-trigger ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''} ${isMulti && selectedOptions.length > 0 ? 'has-values' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="trigger-content">
          {selectedOptions.length === 0 ? (
            <span className="placeholder">{placeholder}</span>
          ) : isMulti ? (
            <div className="selected-tags">
              {selectedOptions.map(opt => (
                <span key={opt.value} className="select-tag">
                  {opt.label}
                  <X size={12} onClick={(e) => handleRemove(e, opt.value)} />
                </span>
              ))}
            </div>
          ) : (
            <span className="selected-value">{selectedOptions[0].label}</span>
          )}
        </div>
        
        <div className="trigger-icons">
          {selectedOptions.length > 0 && !disabled && (
            <X
              size={14}
              className="clear-icon"
              onClick={handleClearAll}
            />
          )}
          <ChevronDown size={18} className={`chevron ${isOpen ? 'rotate' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <div className="searchable-select-dropdown">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              autoFocus
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="options-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = isMulti 
                  ? (Array.isArray(value) && value.includes(option.value))
                  : value === option.value;
                
                return (
                  <div
                    key={option.value}
                    className={`option-item ${isSelected ? 'selected' : ''} ${isMulti ? 'multi-item' : ''}`}
                    onClick={() => handleSelect(option.value)}
                  >
                    {isMulti && (
                      <div className={`checkbox-mini ${isSelected ? 'checked' : ''}`}>
                        {isSelected && <Check size={10} />}
                      </div>
                    )}
                    {option.label}
                  </div>
                );
              })
            ) : (
              <div className="no-options">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
